import { createStore, del, get, set } from 'idb-keyval';

/**
 * Who is signed in, and where the API is.
 *
 * Its own IndexedDB database rather than part of the app's state, because it
 * outlives every screen and must survive anything the rest of the app does to
 * itself. Clearing it signs the person out; nothing else does.
 *
 * Deliberately **not** in the zustand store: `persist` reserialises the whole
 * snapshot on every `set`, and the store is written on every stepper tap. A
 * refresh token has no business being rewritten a hundred times an hour, and
 * `partialize` would be one more thing to remember.
 */
const store = createStore('k2pos-auth', 'credentials');

const KEY = 'device';

export interface Credentials {
  /** Where to sync. Set at enrolment so one build can serve more than one shop. */
  baseUrl: string;
  accessToken: string;
  /** Epoch milliseconds. The client refreshes early rather than on a 401. */
  accessExpiresAt: number;
  refreshToken: string;
  shopId: string;
  shopName: string;
  /**
   * The login that was signed in with. Kept so ຕັ້ງຄ່າຮ້ານ can say who this
   * is without a round trip, and so the change-credentials sheet can show the
   * name it is about to replace. Optional because a credential written by an
   * older build will not have it.
   */
  login?: string;
}

export async function loadCredentials(): Promise<Credentials | undefined> {
  try {
    return await get<Credentials>(KEY, store);
  } catch {
    // A read failure here means "not enrolled" as far as the app is concerned.
    // It must never take down the till, which works offline regardless.
    return undefined;
  }
}

/**
 * Writes the credential and resolves once it is durably on disk.
 *
 * Awaited by the refresh path before the new token is used, so a till that dies
 * mid-rotation retries with the old one — which the server's grace window
 * forgives — rather than with a token it never managed to save.
 */
export async function saveCredentials(credentials: Credentials): Promise<void> {
  await set(KEY, credentials, store);
}

export async function clearCredentials(): Promise<void> {
  await del(KEY, store);
}

/**
 * Where the API is. Empty means "this origin", which is the normal case.
 *
 * The app and the API are always served from the same host — nginx proxies
 * `/api/` to Django in production, and the Vite dev server proxies it to :8000.
 * So a relative path is correct everywhere and there is nothing for a
 * shopkeeper to type or get wrong.
 *
 * `VITE_K2_API` exists only for the odd build that has to point somewhere else
 * (a staging server, a phone testing against a laptop). It is a build-time
 * decision, not a question to put in front of a shop.
 */
export const DEFAULT_BASE_URL = (import.meta.env['VITE_K2_API'] as string | undefined) ?? '';
